import React, { FC } from 'react';
import { useQuery } from '@tanstack/react-query';

import { RoverLoading } from '@rover/react-lib/src/components';
import { EngineEnum } from '@rover/rsdk/src/apiClient/latest/model/engineEnum';
import {
  isServiceLanding,
  SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM,
} from '@rover/types/src/ServiceLanding';
import { Http500Error } from '@rover/utilities/HttpError';

import ServiceLandingPageTemplate from '../../templates/ServiceLandingPageTemplate/ServiceLandingPageTemplate';
import { ServiceLandingContainerProps, ServiceLandingEngineContainer } from '../../types';
import { getServiceLandingSearchQueryOptions } from '../../utilities/getServiceLandingSearchQueryOptions';
import { getServiceLandingFaqsPrefetchQueryOptions } from '../../utils/useServiceLandingFaqs';
import useServiceLandingPageData from '../../utils/useServiceLandingPageData';

const ServiceLandingContainer: FC<ServiceLandingContainerProps> = ({
  serviceLandingData,
  descriptor,
}) => {
  const {
    citiesAndNeighborhoods,
    countryConfig,
    countryFooterLinks,
    countryRegionLinks,
    locale,
    serviceTypes,
    isLoading,
    isError,
  } = useServiceLandingPageData(descriptor, serviceLandingData);

  const { data: searchResults } = useQuery(
    getServiceLandingSearchQueryOptions(serviceLandingData, EngineEnum.template)
  );

  if (isError) throw new Http500Error();

  if (isLoading) {
    return (
      <RoverLoading style={{ height: '100vh' }} data-testid="rover-loading" isLoading={isLoading} />
    );
  }

  if (!countryConfig || !citiesAndNeighborhoods || !serviceTypes || !countryRegionLinks) {
    return null;
  }

  // Derive the landing type from the LocalizedServiceKeyword-backed serviceLandingData
  // (its url slug shares the ServiceLandingEnum string values) rather than from the
  // serviceLinks routing array, so the page renders even when service links are missing.
  // TODO: drop the ServiceLandingEnum bridge and consume the LSK data directly.
  const serviceLandingType = isServiceLanding(serviceLandingData.serviceLandingUrlSlug)
    ? serviceLandingData.serviceLandingUrlSlug
    : null;

  // The template engine is only configured for slugs the enum knows.
  if (!serviceLandingType) throw new Http500Error();

  // service_links is already scoped to this page's own brand/country/locale and already
  // excludes this page itself, so it can be passed straight through.
  const otherServiceLinks = serviceLandingData.serviceLinks;

  return (
    <ServiceLandingPageTemplate
      serviceLandingData={serviceLandingData}
      descriptor={descriptor}
      countryConfig={countryConfig}
      locale={locale}
      serviceTypes={serviceTypes}
      citiesAndNeighborhoodsLinks={citiesAndNeighborhoods}
      countryFooterLinks={countryFooterLinks}
      countryRegionLinks={countryRegionLinks}
      serviceTypeSlug={SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM[serviceLandingType]}
      searchResults={searchResults}
      otherServiceLinks={otherServiceLinks}
    />
  );
};

// Prefetches only the FAQs; the search query is client-only by design (see
// getServiceLandingSearchQueryOptions).
const reactQueryPrefetch: ServiceLandingEngineContainer['reactQueryPrefetch'] = (
  queryClient,
  { descriptor, locale }
) => queryClient.prefetchQuery(getServiceLandingFaqsPrefetchQueryOptions(descriptor, locale));

export default Object.assign(ServiceLandingContainer, { reactQueryPrefetch });
